import { Button } from "@/components/core/buttons/button";
import { JournalAccountSelect } from "@/components/invoices/journal-account-select";
import { invoiceJournalItemsQueryOptions, usePostInvoiceJournalEntryMutation } from "@/features/dashboard/api/queries/invoice-query";
import { useUpdateInvoiceJournalOverridesMutation } from "@/features/dashboard/api/queries/journal-accounts-query";
import type { JournalAccountPurpose } from "@/features/dashboard/types/chart-of-accounts";
import { useQuery } from "@tanstack/react-query";

const phpFormat = (n: number) => n.toLocaleString("en-PH", { style: "currency", currency: "PHP" });

interface JournalItemsTabProps {
	invoiceId: string;
}

export function JournalItemsTab({ invoiceId }: JournalItemsTabProps) {
	const { data, isLoading, isError } = useQuery(invoiceJournalItemsQueryOptions(invoiceId));
	const postMutation = usePostInvoiceJournalEntryMutation(invoiceId);
	const overrideMutation = useUpdateInvoiceJournalOverridesMutation(invoiceId);

	if (isLoading) {
		return <div className="py-10 text-center text-sm text-foreground/70">Loading journal items…</div>;
	}

	if (isError || !data) {
		return <div className="py-10 text-center text-sm text-foreground/70">Unable to load journal items.</div>;
	}

	const isPosted = data.status === "posted" && !!data.entry_number;
	const isBooksDraft = data.status === "draft" && !!data.entry_number;
	const isDraft = !isPosted;

	if (data.lines.length === 0) {
		return <div className="py-10 text-center text-sm text-foreground/70">No journal items computed for this invoice.</div>;
	}

	return (
		<div className="pt-2">
			<div className="mb-3 flex items-start justify-between gap-4 text-xs text-foreground/60">
				<div className="space-y-1">
					<div>
						JE number: <span className="font-mono text-foreground-dark">{data.entry_number ?? "— (draft)"}</span>
					</div>
					<div>
						Entry date: <span className="text-foreground-dark">{data.entry_date ?? "—"}</span>
					</div>
					{data.posted_at && (
						<div>
							Posted at: <span className="text-foreground-dark">{new Date(data.posted_at).toLocaleString("en-PH")}</span>
						</div>
					)}
				</div>
				<div className="flex flex-col items-end gap-2">
					<div className="flex items-center gap-2">
						<StatusBadge posted={isPosted} booksDraft={isBooksDraft} />
						<span className={data.is_balanced ? "text-green-700" : "font-semibold text-red-600"}>{data.is_balanced ? "Balanced" : "Unbalanced"}</span>
					</div>
					{isDraft && (
						<Button
							size="sm"
							onClick={() => postMutation.mutate("draft")}
							disabled={postMutation.isPending || !data.is_balanced}
							title={
								isBooksDraft
									? "Replace the draft in Books with the current local edits."
									: "Push the JE to Books as a draft. Posting to the GL happens inside Books."
							}
						>
							{postMutation.isPending ? "Sending…" : isBooksDraft ? "Re-send to Books" : "Send to Books"}
						</Button>
					)}
					{isBooksDraft && (
						<span className="text-xs text-blue-700">
							Already in Books as <span className="font-mono">{data.entry_number}</span>. Post it from Books.
						</span>
					)}
				</div>
			</div>

			{data.last_error && (
				<div className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">Last attempt failed: {data.last_error}</div>
			)}

			<table className="w-full border-collapse text-sm">
				<thead className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wide text-foreground/60">
					<tr>
						<th className="py-2 pr-2 w-[40%]">Account</th>
						<th className="py-2 pr-2">Label</th>
						<th className="py-2 pr-2 text-right">Debit</th>
						<th className="py-2 pr-2 text-right">Credit</th>
						<th className="py-2 pr-2 text-right">Tax</th>
					</tr>
				</thead>
				<tbody className="divide-y divide-slate-100">
					{data.lines.map((line) => (
						<tr key={`${line.purpose ?? line.account_code}-${line.debit}-${line.credit}`} className="hover:bg-slate-50/60">
							<td className="py-2 pr-2">
								{isDraft && line.purpose ? (
									<JournalAccountSelect
										value={line.account_code}
										disabled={overrideMutation.isPending}
										onChange={(code, _name) => overrideMutation.mutate({ [line.purpose as JournalAccountPurpose]: code === line.account_code ? null : code })}
									/>
								) : (
									<span className="whitespace-nowrap">
										<span className="font-mono text-xs text-foreground/60">{line.account_code}</span>{" "}
										<span className="text-foreground-dark">{line.account_name}</span>
									</span>
								)}
							</td>
							<td className="py-2 pr-2 text-foreground/80">{line.description}</td>
							<td className="py-2 pr-2 text-right tabular-nums">{line.debit > 0 ? phpFormat(line.debit) : "—"}</td>
							<td className="py-2 pr-2 text-right tabular-nums">{line.credit > 0 ? phpFormat(line.credit) : "—"}</td>
							<td className="py-2 pr-2 text-right text-xs text-foreground/60">{line.tax_code ?? "—"}</td>
						</tr>
					))}
				</tbody>
				<tfoot className="border-t-2 border-slate-300 font-semibold">
					<tr>
						<td colSpan={2} className="py-2 pr-2 text-right text-xs uppercase tracking-wide text-foreground/60">
							Totals
						</td>
						<td className="py-2 pr-2 text-right tabular-nums">{phpFormat(data.total_debit)}</td>
						<td className="py-2 pr-2 text-right tabular-nums">{phpFormat(data.total_credit)}</td>
						<td />
					</tr>
				</tfoot>
			</table>
		</div>
	);
}

function StatusBadge({ posted, booksDraft }: { posted: boolean; booksDraft?: boolean }) {
	if (posted) {
		return <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-green-800">Posted</span>;
	}
	if (booksDraft) {
		return <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-blue-800">Draft in Books</span>;
	}
	return <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-amber-800">Draft</span>;
}
